<template>
  <div
    class="asset-item"
    @click="$emit('trigger-asset-modal', asset)"
  >
    <div class="asset-image-wrapper">
      <img
        v-if="imageUrl"
        :src="imageUrl"
        :alt="asset.displayTitle"
        :style="{ width: imageWidth }"
        class="asset-image"
      />
      <icon
        v-else
        :name="assetIcon"
        :variant="assetVariant"
        class="asset-image"
      ></icon>
    </div>
    <div class="asset-content">
      <div class="asset-title">
        {{ displayTitle }}
      </div>
      <div
        v-if="metadataTime"
        class="asset-metadata"
      >
        <icon
          name="video-camera"
          variant="4B5565"
        ></icon>
        <span>{{ metadataTime }}</span>
      </div>
    </div>
    <div class="spacer"></div>
    <div
      class="bookmark"
      @click.stop="toggleBookmark"
    >
      <icon
        :name="hasBookmark ? 'bookmark-icon-filled' : 'bookmark'"
        :variant="hasBookmark ? '' : '4B5565'"
        data-test="asset-bookmark-icon"
      ></icon>
    </div>
    <div
      class="arrow"
    >
      <icon
        name="arrow-narrow-right"
      ></icon>
    </div>
  </div>
</template>

<script>
import Icon from '@components/singletons/icon.vue';
import { stripHtml } from '@/utils';

export default {
  name: 'AssetItem',
  components: {
    Icon,
  },
  props: {
    asset: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['trigger-asset-modal'],
  computed: {
    bookmark() {
      return this.$store.getters['bookmarks/get'][`${this.assetType}-${this.asset.id}`];
    },
    assetIcon() {
      if (this.assetType === 'Topic') {
        return 'topic';
      }
      return '';
    },
    assetType() {
      return this.asset.assetType;
    },
    assetVariant() {
      if (this.assetType === 'Topic') {
        return '';
      } else {
        return '4B5565';
      }
    },
    attachmentType() {
      const type = this.asset.type?.split('/')[0];
      return type || 'attachment';
    },
    displayTitle() {
      const title = this.assetType === 'Topic' ? this.asset.title : this.asset.displayTitle;
      return stripHtml(title);
    },
    hasBookmark() {
      return !!(this.bookmark && !this.bookmark.deleted);
    },
    imageWidth() {
      if (!this.asset.metadata?.width || !this.asset.metadata?.height) {
        return 40;
      }
      return (40 * this.asset.metadata.width / this.asset.metadata.height) + 'px';
    },
    imageUrl() {
      if (this.attachmentType === 'video' && this.asset.thumbnails) {
        return this.asset.thumbnails.small;
      }

      if (this.attachmentType === 'image' && this.asset.url) {
        return this.asset.url;
      }

      if (this.assetType === 'Topic') {
        return null;
      }

      return this.$store.getters['app/appLogo'];
    },
    metadataTime() {
      if(!this.asset.metadata?.duration) return null;
      const duration = Number(this.asset.metadata.duration);
      const minutes = Math.floor(duration / 60);
      const seconds = Math.round(duration % 60);
      return `${minutes}m ${seconds}s`;
    },
  },
  methods: {
    createBookmark() {
      const params = {
        bookmarkableId: this.asset.id,
        bookmarkableType: this.assetType,
        contextType: 'Rationale',
        mobileCreatedAt: Date.now(),
        deleted: false,
      };
      this.$store.dispatch('bookmarks/createBookmark', params);
      this.sendAnalytics(true);
    },
    toggleBookmark() {
      if (this.bookmark) {
        this.updateBookmark();
      } else {
        this.createBookmark();
      }
    },
    updateBookmark() {
      const params = {
        id: this.bookmark.id,
        deleted: !this.bookmark.deleted,
      };
      this.$store.dispatch('bookmarks/updateBookmark', params);
      this.sendAnalytics(!params.deleted);
    },
    sendAnalytics(isBookmarked) {
      const analytics = {
        assetId: this.asset.id,
        assetTitle: this.displayTitle,
        assetType: this.assetType,
      };
      const source = {}; // No source context available in this component

      if (isBookmarked) {
        this.$analytics.events.bookmarkedAsset(analytics, source);
      } else {
        this.$analytics.events.removedBookmark(analytics, source);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.asset-item {
  padding: rem-calc(12);
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e3e8ef;
  justify-content: space-between;
  gap: rem-calc(12);
  cursor: pointer;

  &:last-child {
    border-bottom: none;

    @include breakpoint(desktop) {
      border-bottom: 1px solid #e3e8ef;
    }
  }

  .spacer {
    flex: 1;
  }

  .asset-content {
    line-height: rem-calc(24);

    .asset-title {
      font-size: rem-calc(14);
      font-weight: 500;
      color: #121926;
      line-height: rem-calc(20);
    }

    .asset-metadata {
      display: flex;
      align-items: center;
      gap: rem-calc(4);
      font-size: rem-calc(14);
      color: #4B5565;
    }
  }

  .asset-image-wrapper {
    width: rem-calc(64);
    min-width: rem-calc(64);
    display: grid;
    place-items: center;
  }

  .bookmark,
  .arrow {
    width: rem-calc(24);
    min-width: rem-calc(24);
  }
}
</style>
